import { ActionTree, ActionContext } from 'vuex';
import { calendarClient } from '@/http-client';
import { camelToSnake, snakeToCamel } from '@/utils';
import { RootStateI } from '../state';
import { SubscriptionStateI, IcsSubscriptionI } from './state';

export interface SubscribePayloadI {
  calendarId: string | number;
  url: string;
}

export interface ApiSubscriptionResponseI {
  id: string;
  calendarId: string | number;
  url: string;
  status: 'pending' | 'syncing' | 'active' | 'failed';
  lastSyncAt?: number;
  createdAt: number;
  error?: string;
}

export const actions: ActionTree<SubscriptionStateI, RootStateI> = {
  async subscribe(
    context: ActionContext<SubscriptionStateI, RootStateI>,
    payload: SubscribePayloadI,
  ): Promise<IcsSubscriptionI> {
    context.commit('setLoading', true);
    context.commit('setError', null);

    try {
      const { data } = await calendarClient.post(
        '/api/events/subscribe',
        camelToSnake(payload),
      );

      const subscription = snakeToCamel(data) as IcsSubscriptionI;
      context.commit('addSubscription', subscription);
      return subscription;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message
        || error.message
        || 'Failed to subscribe to ICS feed';

      context.commit('setError', errorMessage);
      throw error;
    } finally {
      context.commit('setLoading', false);
    }
  },

  async listSubscriptions(
    context: ActionContext<SubscriptionStateI, RootStateI>,
    calendarId?: string | number,
  ): Promise<IcsSubscriptionI[]> {
    context.commit('setLoading', true);
    context.commit('setError', null);

    try {
      const url = calendarId
        ? `/api/events/subscribe?calendar_id=${calendarId}`
        : '/api/events/subscribe';

      const { data } = await calendarClient.get(url);
      const subscriptions = Array.isArray(data)
        ? (snakeToCamel(data) as IcsSubscriptionI[])
        : [];

      context.commit('setSubscriptions', subscriptions);
      return subscriptions;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message
        || error.message
        || 'Failed to fetch subscriptions';

      context.commit('setError', errorMessage);
      throw error;
    } finally {
      context.commit('setLoading', false);
    }
  },

  async unsubscribe(
    context: ActionContext<SubscriptionStateI, RootStateI>,
    subscriptionId: string,
  ): Promise<void> {
    context.commit('setLoading', true);
    context.commit('setError', null);

    try {
      await calendarClient.delete(`/api/events/subscribe/${subscriptionId}`);
      context.commit('removeSubscription', subscriptionId);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message
        || error.message
        || 'Failed to unsubscribe from ICS feed';

      context.commit('setError', errorMessage);
      throw error;
    } finally {
      context.commit('setLoading', false);
    }
  },

  async syncSubscription(
    context: ActionContext<SubscriptionStateI, RootStateI>,
    subscriptionId: string,
  ): Promise<void> {
    context.commit('setLoading', true);
    context.commit('setError', null);

    try {
      const { data } = await calendarClient.post(
        `/api/events/subscribe/${subscriptionId}/sync`,
      );

      const subscription = snakeToCamel(data) as IcsSubscriptionI;
      context.commit('updateSubscription', {
        id: subscriptionId,
        updates: subscription,
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message
        || error.message
        || 'Failed to sync ICS feed';

      context.commit('setError', errorMessage);
      throw error;
    } finally {
      context.commit('setLoading', false);
    }
  },

  clearError(context: ActionContext<SubscriptionStateI, RootStateI>): void {
    context.commit('clearError');
  },
};
